// Mobile-safe clipboard copy.
//
// CRITICAL ordering: try the legacy textarea + execCommand fallback FIRST,
// then the modern navigator.clipboard.writeText() second. Counter-intuitive,
// but it's the only order that works reliably across iOS Safari and mobile
// in-app webviews (Telegram, Instagram, Facebook).
//
// Why: iOS Safari requires copy operations to happen SYNCHRONOUSLY within
// the user's tap event. The modern `navigator.clipboard.writeText()` is
// async — by the time you `await` it (or even just `.then()` it), the user-
// gesture context has elapsed. If that path then rejects (HTTPS check fails,
// permission denied, in-app webview restriction), the fallback execCommand
// runs OUTSIDE a gesture and silently returns false. Net result: nothing
// copies, no feedback, button looks broken.
//
// The legacy execCommand path is synchronous, preserves the gesture, and
// works on basically every browser made in the last decade. We try it
// first; only when it returns false (or throws) do we fall back to the
// async modern API as a last resort.
//
// Returns true on success, false on total failure.

function legacyCopy(text: string): boolean {
  if (typeof document === "undefined") return false;
  const ta = document.createElement("textarea");
  ta.value = text;

  // iOS Safari quirks:
  //   - readonly + contentEditable lets selection actually fire
  //   - position fixed off-screen (not display:none / opacity:0, which
  //     break selection on iOS)
  //   - non-zero size (1x1px) so the element is "selectable" enough
  ta.setAttribute("readonly", "");
  ta.contentEditable = "true";
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.width = "1px";
  ta.style.height = "1px";
  ta.style.padding = "0";
  ta.style.border = "none";
  ta.style.outline = "none";
  ta.style.boxShadow = "none";
  ta.style.background = "transparent";
  document.body.appendChild(ta);

  // Save the current selection so we don't disrupt anything the user had
  // selected before tapping copy.
  const sel = document.getSelection();
  const savedRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;

  // iOS needs Range-based selection rather than plain .select() on a
  // hidden / off-screen textarea.
  ta.focus();
  ta.setSelectionRange(0, text.length);
  const range = document.createRange();
  range.selectNodeContents(ta);
  sel?.removeAllRanges();
  sel?.addRange(range);
  ta.setSelectionRange(0, text.length);

  let ok = false;
  try {
    // execCommand is deprecated but still ships in every browser. Returns
    // false if the user agent refuses to copy (rare).
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }

  document.body.removeChild(ta);

  // Restore the user's previous selection so we don't visibly disturb the
  // page.
  if (savedRange) {
    sel?.removeAllRanges();
    sel?.addRange(savedRange);
  }

  return ok;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  // Path 1 — synchronous legacy fallback. Works in user-gesture context on
  // iOS Safari, in-app webviews, and HTTP previews.
  if (legacyCopy(text)) return true;

  // Path 2 — modern async API. Reached only when legacy returned false
  // (e.g. on browsers that have removed execCommand support entirely; rare).
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // both paths exhausted
    }
  }

  return false;
}
